import React, { useState, useEffect } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { get, size, set } from 'global/lib/lodash';
import useStyles from './assessment.styles';
import { Text } from 'global/components/text';
import { Column, Row } from 'global/components/row';
import { Button } from 'global/components/Button';
import Image from 'global/components/image';
import { Checkbox, Icon, Tooltip } from '@mui/material';
import SnackAlerts from 'global/components/snack-alerts';
import { scrollTop, _size, _first, ensureArray } from 'global/utils';
import clsx from 'clsx';
import sendData from 'redux/actions/sendData';
import { withRouter } from 'react-router-dom';
import { Loader, useScreen } from 'global/styles';
import ReportAssessmentScoreContent from '../../report/AssessmentScore';
import { PScoreIncludes } from '../../report/Assessment/ReportAssessment';
import colors from 'global/styles/colors';
import NavToTop from 'feature/nav/NavToTop';
import { formatSubScore } from '../../report/AssessmentScore/SubScore/SubScoreLauncher';

export const renderTitle = (status, opts = {}) => {
  const stattitles = [
    { key: 'created', title: 'Not Started' },
    { key: 'draft', title: 'Started' },
    { key: 'started', title: 'Started' },
    { key: 'revise', title: 'Needs Revisions' },
    { key: 'submitted', title: 'Submitted', rtitle: 'Pending your review' },
    { key: 'approved', title: 'Approved' },
    { key: 'completed', title: 'Completed' },
  ];

  const found = stattitles.find((st) => st.key === status);
  return get(found, opts.isReview ? 'rtitle' : 'title', get(found, 'title'));
};

export const renderButtonInfo = (status, opts = {}) => {
  const { type, isReview } = opts;
  const texts = [
    { key: 'created' },
    { key: 'draft', tip: 'You must fill out all the items in the assessment before submitting' },
    {
      key: 'started',
      buttonTitle: 'Ready to Submit',
      tip: 'You must fill out all the items in the assessment to proceed',
    },
    {
      key: 'revise',
      buttonTitle: 'Re-Submit',
      tip: 'Ready to re-submit for approval',
      rtip: 'The student must re-submit this assessment for you to proceed',
    },
    {
      key: 'submitted',
      rButtonTitle: 'Done Reviewing',
      tip: 'The reviewer must respond to your assessment for you to proceed',
      rtip: 'Make sure you reviewed everything',
    },
    {
      key: 'approved',
      buttonTitle: 'I\'m Finished!',
      rButtonTitle: 'Submit',
      tip: 'Please review any notes before proceeding',
      rtip: 'The student must acknowledge your notes',
    },
    { key: 'completed', tip: 'This assessment is complete', rtip: 'This assessment is complete' },
  ];

  const found = texts.find((st) => st.key === status);
  if (type === 'tooltip') {
    return get(found, isReview ? 'rTip' : 'tip', '');
  } else {
    return get(found, isReview ? 'rButtonTitle' : 'buttonTitle', 'Submit');
  }
};

export const renderStudentText = (status, name) => {
  if (['submitted'].indexOf(status) > -1) {
    return `Your reviewer, ${name}, will go over these responses and either approve them or return them for revisions.`;
  } else if (['revise'].indexOf(status) > -1) {
    return `Your reviewer has asked you to revise your responses. Read their comments below, make changes, and re-submit at the bottom of the page.`;
  } else if (['approved'].indexOf(status) > -1) {
    return 'Your reviewer has approved your answers! Review any comments and acknowledge the updates at the bottom of the page.';
  } else if (['completed'].indexOf(status) > -1) {
    return 'This assessment is completed!';
  }

  return ``;
};

export const renderReviewerText = (status, name) => {
  if (['submitted'].indexOf(status) > -1) {
    return `This assessment is ready for your review! Add comments below, and update the status at the bottom of the page.`;
  } else if (['revise'].indexOf(status) > -1) {
    return 'This assessment has been sent back to the student for revisions.';
  } else if (['approved'].indexOf(status) > -1) {
    return 'The student must review and accept the comments on this assessment to complete the course.';
  }

  return ``;
};

export const Assessment = withRouter((props) => {
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });
  const isReview = get(props, 'location.state.isReview', false);

  const { updateReadStatus } = props;

  const courseassessment = get(props, 'documents.assessment', {}) || {};
  const assessment_id = get(courseassessment, 'id');
  const assessmentStatus = get(courseassessment, 'status');

  // const course_assignment_id = get(props, 'documents.id');

  const [state, setState] = useState({
    assessment_id,
    currentQuestion: 0,
    answers: {},
    showResults: false,
    scoreValue: 0,
    showReviewerSubmit: false,
    canSubmit: false,
  });
  const [alerts, setAlerts] = useState();

  const assessment = useRetrieveData(
    {
      url: `/assessments/${assessment_id}`,
    },
    {
      build: ['assessments'],
      transformData: (d) => {
        const v = d[0];
        return { ...v, availableStatus: Object.keys(v.availableStatus || []).map(k => v.availableStatus[k]) }
      },
      params: {
        include: ['realm', 'realm.realms', 'realm.parent', 'scorecards', 'authors', 'editors', 'viewers', 'media', 'realmPhotos', 'scorecardScores', 'scorecardScores.comments'].join(','),
      },
    },
  );
  const scores = useRetrieveData(
    {
      url: '/processed-scorecard-score-values',
    },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: assessment_id },
        include: PScoreIncludes,
      },
      transformData: (d) => {
        const scdata = {};
        d.map((di) => {
          if (size(di.subScores)) {
            //note subscores is used instead of subScores, and this should hold true throughout
            //the rest of the site. Since we can't overwrite subScores since it doens't have a setter
            di.subscores = di.subScores.map((s) => formatSubScore(s, di));
          }
          set(scdata, di.scorecardId, [...get(scdata, di.scorecardId, []), di]);
        });
        return scdata;
      },
    },
  );

  const reviewername = get(assessment, 'data.editors.0.name', 'N/A');

  const { update, loading } = assessment;

  const changeQuestion = (dir = 1) => {
    setState((s) => ({ ...s, currentQuestion: s.currentQuestion + dir }));
  };

  const submit = async (stat = 'submitted') => {
    try {
      setState((s) => ({ ...s, loading: true }));

      if (isReview) {
        if (!state.showReviewerSubmit) {
          setState((s) => ({ ...s, showReviewerSubmit: true }));
        } else {
          await updateAssessmentStatus(stat);

          props.history.push('/library');
        }
      } else {
        if (assessmentStatus === 'approved') {
          stat = 'completed';
        }

        if (stat === 'completed') {
          updateReadStatus(true);
          await updateAssessmentStatus(stat);

          setState((s) => ({ ...s, currentQuestion: 2, canSubmit: false }));
          setAlerts({ message: `Your course is complete!`, severity: 'success' });
        } else if (state.currentQuestion < 2) {
          setState((s) => ({ ...s, currentQuestion: s.currentQuestion + 1 }));
        } else {
          await updateAssessmentStatus(stat);

          await Promise.all([props.update && props.update(), update()]);

          setState((s) => ({ ...s, showResults: true, currentQuestion: 0, canSubmit: false }));
          setAlerts({ message: `Your assessment has been submitted for review!`, severity: 'success' });
        }
      }
    } catch (err) {
      console.error(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const updateAssessmentStatus = async (status) => {
    try {
      await sendData({
        url: `/assessments/${assessment_id}`,
        method: 'patch',
        data: {
          data: {
            type: 'assessments',
            id: assessment_id,
            attributes: {
              status,
            },
          },
        },
      });
      (await props.update) && props.update();
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    }
  };

  const getLastSlide = () => {
    if (assessmentStatus === 'completed') {
      return {
        className: 'intro',
        title: `Assessment`,
        description: `Congrats! You've now completed this assessment and this course! You can come back to this page later and review your responses if you'd like.`,
      };
    } else {
      return {
        className: 'intro',
        title: `Assessment`,
        description: `When you submit your assessment, it will be sent to your reviewer ${reviewername}. Once they have reviewed it, you'll see their comments and either be asked to make revisions, or be marked as complete.`,
      };
    }
  };

  const getFirstSlide = () => {
    return {
      className: 'intro',
      title: 'Assessment',
      buttons: [{ title: 'BEGIN', onClick: () => changeQuestion(1), startIcon: <Icon>arrow_right</Icon> }],
      description: `There is one remaining step to demonstrate that you understand the material in this course.
<br />
<br />
You will be shown a series of checkpoints. For each, you'll need to find an example, upload a photo(s), rate that example based on your knowledge, and briefly describe why you chose that rating.
<br />
<br />
You <b>do <i>not</i> need to find examples of everything done well.</b> The goal is to demonstrate you understand what to look for - so poor examples are OK so long as you rate them as poor and correctly explain why.
<br />
<br />
You <b>do <i>not</i> need to finish in one sitting.</b> Whenever you visit this page, the work you had done previously will still be there. So take your time, and find the best examples of either good or bad practices.
<br />
<br />
Once finished, the assessment will go to your instructor for approval and feedback.
`,
    };
  };

  // const onToggleEdit = activeEdit => {
  //   console.log({ activeEdit });
  // }

  const onCommentSave = async () => {
    await update({ loading: false });
  };

  const onChange = (e) => {
    const ad = ensureArray(_first(e.allProcessedScoreData || []));
    const all = ad.filter((a) => a.parentId);
    const allFilled = all.filter((a) => !!a.value);
    if (loading || !_size(e.allProcessedScoreData)) {
      return;
    }

    if (!isReview) {
      const hasAll = _size(allFilled) >= _size(all) && _size(allFilled) > 0;
      const validStatus = ['started', 'draft', 'revise', 'approved'].includes(assessmentStatus);

      setState((s) => ({ ...s, canSubmit: validStatus && hasAll }));
    } else {
      setState((s) => ({ ...s, canSubmit: ['submitted'].includes(assessmentStatus) }));
    }
  };

  useEffect(() => {
    const isStarting = ['created', 'draft'].indexOf(get(courseassessment, 'status')) > -1;

    if (!isReview) {
      if (state.currentQuestion === 1 && isStarting) {
        updateAssessmentStatus('started');
      } else if (state.currentQuestion === 0 && !isStarting) {
        setState((s) => ({ ...s, currentQuestion: 1 }));
      }
    } else if (isReview && state.currentQuestion === 0) {
      setState((s) => ({ ...s, currentQuestion: 1 }));
    }
  }, [state.currentQuestion]);

  const showQ = state.currentQuestion === 0 && !isReview;

  if (isReview && ['created', 'started', 'draft'].indexOf(assessmentStatus) > -1) {
    return (
      <div className={classes.questionsContainer}>
        <Question
          currentQuestion={state.currentQuestion}
          answer={get(state.answers, state.currentQuestion, {})}
          question={{
            className: 'intro',
            title: `Assessment`,
            description: `This assessment is ${renderTitle(assessmentStatus)} and not yet available for review.`,
          }}
        />
      </div>
    );
  }

  if (state.showReviewerSubmit) {
    return (
      <div className={classes.questionsContainer}>
        <Question
          currentQuestion={0}
          answer={{}}
          question={{
            className: 'assessmentEnd',
            title: `Assessment`,
            description: `What is the result of your review?`,
            buttons: [
              {
                title: 'THE STUDENT HAS PASSED THE COURSE',
                onClick: () => submit('approved'),
                // color: colors,
                startIcon: <Icon>checkmark</Icon>,
                helperText: `This button will notify the student that they've passed, but they must still read your comments  and accept them to complete the course.`,
              },
              {
                title: 'THIS ASSESSMENT NEEDS REVISIONS',
                onClick: () => submit('revise'),
                bgColor: colors.red,
                startIcon: <Icon>close</Icon>,
                helperText: `This button will notify the student that their assessment requires revisions for them to pass the course. They will have to make changes and re-submit their results.`,
              },
              {
                title: 'GO BACK',
                onClick: () => setState((s) => ({ ...s, showReviewerSubmit: false })),
                bgColor: colors.black,
                startIcon: <Icon>arrow_left</Icon>,
                starterText: 'Not done yet?',
              },
            ],
          }}
        />
      </div>
    );
  }

  //so state.currentQuestion: 0 -> views['intro']
  // const views = [
  //   { key: 'intro' },
  //   { key: 'assessment' },
  //   { key: 'recap' },
  // ]

  return loading ? (
    <Column className={classes.questionsContainer}>
      <Loader />
    </Column>
  ) : (
    <div className={classes.questionsContainer}>
      {showQ && <Question currentQuestion={state.currentQuestion} answer={get(state.answers, state.currentQuestion, {})}
                          question={getFirstSlide()} />}

      {!showQ && (
        <div className="assessmentContainer">
          <NavToTop visible onClick={() => scrollTop('.assessmentContainer')} />

          {(isReview || state.currentQuestion < 2) && (
            <Column center className="assessmentTitle">
              <Text type="title_large">{`Assessment`}</Text>
              <Text>
                {`Status: `}
                <em>{renderTitle(assessmentStatus, { isReview })}</em>
              </Text>
              <Text>{isReview ? renderReviewerText(assessmentStatus, reviewername) : renderStudentText(assessmentStatus, reviewername)}</Text>
            </Column>
          )}

          <div style={state.currentQuestion !== 1 ? { left: -99999, position: 'absolute' } : {}}>
            <ReportAssessmentScoreContent
              assessment={assessment}
              isTemplate={false}
              canShowScores={true}
              scores={scores}
              userCanEdit={assessmentStatus !== 'completed'}
              isCourse
              // onToggleEdit={onToggleEdit}
              isReview={isReview}
              onCommentSave={onCommentSave}
              onChange={onChange}
            />
          </div>

          <Column center>
            {!isReview && state.currentQuestion > 1 && <Question question={getLastSlide()} />}
            <Row center className="buttonRow">
              {!isReview && state.currentQuestion === 2 && (
                <Button disabled={state.currentQuestion <= 1} onClick={() => changeQuestion(-1)}
                        startIcon={<Icon>arrow_left</Icon>}>
                  Back
                </Button>
              )}
              <Tooltip title={state.currentQuestion > 0 && !state.canSubmit ? renderButtonInfo(assessmentStatus, {
                type: 'tooltip',
                isReview,
              }) : ''}>
                <span>
                  <Button disabled={state.loading || (state.currentQuestion > 0 && !state.canSubmit)}
                          loading={state.loading} onClick={() => submit()} startIcon={<Icon>arrow_right</Icon>}>
                    {state.currentQuestion > 1 ? 'SUBMIT' : renderButtonInfo(assessmentStatus, {
                      type: 'button',
                      isReview,
                    })}
                  </Button>
                </span>
              </Tooltip>
            </Row>
          </Column>
          <div style={{ height: 50 }} />
        </div>
      )}

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
});

const Question = (props) => {
  const { question, currentQuestion, answer, setAnswer, showAnswer } = props;

  const { checkpointOptions } = question;

  const images = get(question, 'images', []);
  const hasImages = !!_size(images);
  const isBoolQ = _size(checkpointOptions) === 2 && !!checkpointOptions.find((cpo) => cpo.name === 'True');

  const title = (() => {
    if (question.title) {
      return typeof question.title === 'function' ? question.title() : question.title;
    } else {
      return `Question ${currentQuestion + 1}`;
    }
  })();

  const __html = (() => {
    const d = get(question, 'description', '');
    return typeof d === 'function' ? d() : d;
  })();

  const isCorrect = (cpo) => {
    return get(cpo, 'value', 0) === 100;
  };

  const renderIcon = (cpo) => {
    const correct = isCorrect(cpo);
    if (showAnswer) {
      return <Icon>{correct ? 'check_circle' : 'cancel'}</Icon>;
    } else {
      return <Checkbox checked={get(answer, 'id') === cpo.id} />;
    }
  };

  const buttons = get(question, 'buttons', []);

  return (
    <div className={clsx('question', get(question, 'className', ''), showAnswer ? 'showAnswer' : '')}>
      <Text className="header" type="title_large">
        {title}
      </Text>
      <Text className="description" type="title_small" dangerouslySetInnerHTML={{ __html }} />

      {hasImages && (
        <div>
          {images.map((img) => (
            <Image src={img.url} />
          ))}
        </div>
      )}

      {!!_size(buttons) && (
        <Row className="submitRow" center>
          {buttons.map((btn) => (
            <Column className="btn">
              {btn.starterText && <Text type="label_small">{btn.starterText}</Text>}
              <Button {...btn}>{btn.title}</Button>
              {btn.helperText && <Text type="label_small">{btn.helperText}</Text>}
            </Column>
          ))}
        </Row>
      )}

      <div className={clsx('options', isBoolQ && 'boolean')}>
        {(checkpointOptions || []).map((cpo) => {
          const correct = isCorrect(cpo);
          const isSelected = get(answer, 'id') === cpo.id;
          return (
            <Row
              className={clsx('checkpointOption', isSelected && 'selected', showAnswer && correct ? 'correct' : 'incorrect')}
              onClick={setAnswer ? () => setAnswer(cpo) : () => 0}>
              {renderIcon(cpo)}
              <Text type="title_small">{cpo.name}</Text>
            </Row>
          );
        })}
      </div>
    </div>
  );
};
